import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { borderDarkGrey, transparentYellowBox, universalStyles } from '../../../../app/theme';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import FindingsPanel from '../../../micro/FindingsPanel/FindingsPanel';
import CaseStudy from '../../../micro/caseStudy/CaseStudy';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  header: {
    marginBottom: 30,
  },
  image: {
    width: '100%',
  },
  caption: {
    fontSize: 14,
    color: borderDarkGrey,
    display: 'block',
    marginBottom: 20,
  },
  headerRow: {
    marginTop: 50,
    width: 612,
    overflowX: 'visible',
    borderBottom: `1px solid ${borderDarkGrey}`,
    paddingBottom: 21,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    display: 'inline',
    width: '50vw',
    minWidth: 612,
    marginBottom: 0,
  },
  subtitle: {
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  yellowBox: {
    margin: '0 25px 33px -25px',
    width: '100%',
    padding: '21px 25px',
    backgroundColor: transparentYellowBox,
  },
  imageTitle: {
    marginBottom: 8,
  },
  findings: {
    marginTop: 15,
  },
  row: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  smallColumn: {
    width: '33%',
    marginRight: '2%',
  },
  largeColumn: {
    width: '65%',
  },
}));

type InterpretationPage13Props = {
  setNextDisabled: (disabled: boolean) => void;
  setNextDisabledCS: (disabled: boolean) => void;
};

const InterpretationPage13 = ({ setNextDisabled, setNextDisabledCS }: InterpretationPage13Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3</Typography>
          <Typography variant="h3" className={classes.header}>
            Assess and Evaluate Axumin Uptake in Various Settings (continued)
          </Typography>
          <CaseStudy trainingId={1} module="axuminInterpretation" page={13} setNextDisabledCS={setNextDisabledCS} />
          <Typography variant="h5">Prostate Bed: Intact Prostate With Prior Therapy</Typography>
          <img
            src={`${BASE_URL}${IMAGE_PATHS.PROSTATE_BED_TABLE.path}`}
            alt={IMAGE_PATHS.PROSTATE_BED_TABLE.alt}
            className={classes.image}
          />
          <Typography variant="caption" className={classes.caption}>
            *Uptake not meeting threshold for malignancy (equivocal) may require follow-up and clinical correlation.
          </Typography>
          <ul className={classes.list}>
            <li>
              Uptake on anatomical correlate &lt; 1 cm, significantly greater than blood pool (ie, close to bone
              marrow), may also be considered suspicious for malignancy; MRI correlation is suggested
            </li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>
                If a lesion of this size does not meet this threshold, it should be reported as such but requires
                follow-up and clinical correlation
              </li>
            </ul>
            <li>Focal uptake with calcification may indicate benign inflammation; MRI correlation is suggested</li>
            <li>
              Anecdotally, median prostate lobe uptake (central base invaginating into bladder) has a higher false
              positivity
            </li>
          </ul>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <div className={classes.yellowBox}>
            <Typography variant="body1" className={classes.imageTitle}>
              <b>Axial view</b>
            </Typography>
            <img
              src={`${BASE_URL}${IMAGE_PATHS.AXIAL_VIEW_STEP_3_ARROWS.path}`}
              alt={IMAGE_PATHS.AXIAL_VIEW_STEP_3_ARROWS.alt}
              className={classes.image}
            />
            <Typography variant="body1" className={classes.findings}>
              <b>Findings:</b>
            </Typography>
            <Typography variant="body1">Positive prostate, biopsy proven malignant</Typography>
          </div>
          <div className={classes.yellowBox}>
            <div className={classes.row}>
              <div className={classes.smallColumn}>
                <Typography variant="body1" className={classes.imageTitle}>
                  <b>Axial view</b>
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.AXIAL_VIEW_SINGLE.path}`}
                  alt={IMAGE_PATHS.AXIAL_VIEW_SINGLE.alt}
                  className={classes.image}
                />
              </div>
              <div className={classes.largeColumn}>
                <Typography variant="body1" className={classes.imageTitle}>
                  <b>Coronal view</b>
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.CORONAL_VIEW_STEP_3_ARROWS.path}`}
                  alt={IMAGE_PATHS.CORONAL_VIEW_STEP_3_ARROWS.alt}
                  className={classes.image}
                />
              </div>
            </div>
            <Typography variant="body1" className={classes.findings}>
              <b>Findings:</b>
            </Typography>
            <Typography variant="body1">
              Diffuse homogeneous activity not significantly greater than marrow visually (borderline), biopsy proven
              benign
            </Typography>
          </div>
          <FindingsPanel trainingId={1} module="axuminInterpretation" page={13} />
        </div>
      </div>
      <LearningCheckQuestions
        module="axuminInterpretation"
        page={13}
        setNextDisabled={setNextDisabled}
        trainingId={1}
      />
    </>
  );
};

export default InterpretationPage13;
